import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/SimpleAuthContext';
import API_CONFIG from '../config/apiConfig';
import { CAREER_PASSPORT_DEFINITIONS, getCoursePassportLinks } from '../config/careerPassports';
import { MagnifyingGlassIcon, HeartIcon, ArrowRightIcon, ClockIcon, BuildingOfficeIcon, ComputerDesktopIcon, Squares2X2Icon, Bars3Icon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { courseDuration } from '../utils/courseDisplay';
import './Courses.css';

export const filterCourses = (courses, filters) => courses.filter(course => {
  const search = filters.search.trim().toLowerCase();
  return (!search || [course.programme_name, course.description, ...(course.lms_course_details?.skills || [])].join(' ').toLowerCase().includes(search))
    && (!filters.campus || (course.campus?.code || course.campus) === filters.campus)
    && (!filters.nqf || String(course.nqf_exit_level) === filters.nqf)
    && (!filters.type || course.course_type === filters.type)
    && (!filters.passport || getCoursePassportLinks(course).some(link => link.id === filters.passport));
});
const emptyFilters = { search: '', passport: '', type: '', campus: '', nqf: '' };
const readRows = data => Array.isArray(data) ? data : (data?.results || []);
const learnerHeaders = accessToken => {
  const jwt = accessToken || localStorage.getItem('access_token');
  return { Authorization: jwt ? `Bearer ${jwt}` : `Token ${localStorage.getItem('token') || ''}` };
};

export const ApplicationTag = ({ application }) => application ? (
  <span className={`catalogue-application-tag catalogue-application-tag--${application.status}`}>
    {application.status_display || application.status?.replaceAll('_', ' ')}
  </span>
) : null;

const PassportPath = ({ course }) => {
  const links = getCoursePassportLinks(course);
  return <div className="catalogue-path">
    <div className="catalogue-passports" aria-label="Passport alignment">
      {links.length ? links.map(link => <span key={link.id} className={`catalogue-passport catalogue-passport--${link.tone}`} title={link.coverage}>
        <span aria-hidden="true" />{link.title}
        {link.alignment_source === 'staff_confirmed'
          ? <small>{[link.belt ? `${link.belt} belt` : 'Belt not assigned', link.standard_label || 'Standard not assigned'].join(' · ')}</small>
          : <small>Suggested · needs course mapping</small>}
      </span>) : <span className="catalogue-path-note">{course.passport_alignment_reviewed ? 'No passport track linked' : 'Passport mapping needed'}</span>}
    </div>
    {links.some(link => link.alignment_source === 'inferred_needs_review') && <p className="catalogue-path-note">Suggested from course details. Belt and standard need confirmation.</p>}
  </div>;
};

const CourseCard = ({ course, application, saved, saving, onSave }) => {
  const OnlineIcon = course.is_lms_hosted ? ComputerDesktopIcon : BuildingOfficeIcon;
  return <article className="catalogue-card">
    <div className="catalogue-card-top">
      <span className="catalogue-delivery"><OnlineIcon />{course.is_lms_hosted ? 'Online learning' : course.is_external_provider ? 'Provider training' : 'Campus training'}</span>
      <button type="button" className={`catalogue-save ${saved ? 'is-saved' : ''}`} onClick={() => onSave(course.id)} disabled={saving} aria-label={`${saved ? 'Remove' : 'Save'} ${course.programme_name}`} aria-pressed={saved}>
        {saved ? <HeartSolidIcon /> : <HeartIcon />}
      </button>
    </div>
    <h2 className="catalogue-course-title"><Link to={`/courses/${course.id}`}>{course.programme_name}</Link></h2>
    <ApplicationTag application={application} />
    <PassportPath course={course} />
    <p className="catalogue-description">{course.description || 'Explore the course details for programme information and entry requirements.'}</p>
    <div className="catalogue-meta">
      <span><ClockIcon />{courseDuration(course)}</span>
      <span><OnlineIcon />{course.is_lms_hosted ? 'LMS Online' : course.campus?.name || 'Location to be confirmed'}</span>
      {course.nqf_exit_level && <span><AcademicCapIcon />NQF {course.nqf_exit_level}</span>}
      {course.accreditation_status === 'approved' && <span className="catalogue-accredited">Accredited</span>}
    </div>
    <div className="catalogue-card-actions">
      {application && <Link className="catalogue-secondary-action" to="/dashboard">View application</Link>}
      <Link className="catalogue-primary-action" to={`/courses/${course.id}`}>View course <ArrowRightIcon /></Link>
    </div>
    <span className="catalogue-availability">{!course.is_lms_hosted && !application ? (course.can_register ? 'Applications open' : course.is_full ? 'Course full' : 'Applications currently closed') : ''}</span>
  </article>;
};

const Courses = () => {
  const { isAuthenticated, accessToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [layout, setLayout] = useState('cards');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wishlist, setWishlist] = useState({});
  const [registrations, setRegistrations] = useState({});
  const [saving, setSaving] = useState(null);
  const [notice, setNotice] = useState('');
  const [limit, setLimit] = useState(24);
  const [moreFilters, setMoreFilters] = useState(false);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(API_CONFIG.courses.list(), { params: { page_size: 1000 } });
      const unique = new Map(readRows(response.data).map(course => [course.id, course]));
      setCourses([...unique.values()]);
    } catch {
      setError('We could not load the courses. Please check your connection and try again.');
    } finally { setLoading(false); }
  }, []);

  const loadLearnerState = useCallback(async () => {
    if (!isAuthenticated) { setWishlist({}); setRegistrations({}); return; }
    const headers = learnerHeaders(accessToken);
    const [saved, applications] = await Promise.allSettled([
      axios.get(API_CONFIG.registrations.wishlist(), { headers }),
      axios.get(API_CONFIG.registrations.registrationStatus(), { headers })
    ]);
    if (saved.status === 'fulfilled') setWishlist(Object.fromEntries(readRows(saved.value.data).map(item => [item.course.id, item.id])));
    if (applications.status === 'fulfilled') setRegistrations(applications.value.data);
  }, [isAuthenticated, accessToken]);

  useEffect(() => { loadCourses(); }, [loadCourses]);
  useEffect(() => { loadLearnerState(); }, [loadLearnerState]);
  useEffect(() => {
    const refresh = () => Promise.all([loadCourses(), loadLearnerState()]);
    window.refreshCourseData = refresh;
    return () => { if (window.refreshCourseData === refresh) delete window.refreshCourseData; };
  }, [loadCourses, loadLearnerState]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const filtered = useMemo(() => filterCourses(courses, filters), [courses, filters]);
  const campuses = useMemo(() => [...new Map(courses.filter(course => course.campus?.code).map(course => [course.campus.code, course.campus])).values()], [courses]);
  const levels = useMemo(() => [...new Set(courses.map(course => course.nqf_exit_level).filter(Boolean))].sort((a, b) => Number(a) - Number(b)), [courses]);
  const hasFilters = Object.values(filters).some(Boolean);
  const updateFilter = (field, value) => { setFilters(previous => ({ ...previous, [field]: value })); setLimit(24); };
  const clearFilters = () => { setFilters(emptyFilters); setLimit(24); };

  const toggleSave = async courseId => {
    if (!isAuthenticated) { setNotice('Sign in to save courses to your wishlist.'); return; }
    setSaving(courseId);
    try {
      const headers = learnerHeaders(accessToken);
      if (wishlist[courseId]) {
        await axios.delete(`${API_CONFIG.registrations.wishlist()}${wishlist[courseId]}/`, { headers });
        setWishlist(previous => { const next = { ...previous }; delete next[courseId]; return next; });
        setNotice('Course removed from your wishlist.');
      } else {
        const response = await axios.post(API_CONFIG.registrations.wishlist(), { course_id: courseId, notify_on_opening: true, notify_on_capacity: true }, { headers });
        if (response.data.id) setWishlist(previous => ({ ...previous, [courseId]: response.data.id }));
        else await loadLearnerState();
        setNotice('Course saved to your wishlist.');
      }
    } catch (err) { setNotice(err.response?.status === 401 ? 'Your session has expired. Please sign in again.' : 'We could not update your wishlist. Please try again.'); }
    finally { setSaving(null); }
  };

  return <div className="course-catalogue">
    <div className="catalogue-container">
      <header className="catalogue-heading">
        <div><h1>Find your next course</h1><p>Explore training, choose a skills passport and take your next step.</p></div>
        <Link to="/career" className="catalogue-guidance">Not sure where to start? <span>Try career guidance <ArrowRightIcon /></span></Link>
      </header>
      <section className="catalogue-filters" aria-label="Filter courses">
        <div className="catalogue-search">
          <label className="catalogue-sr-only" htmlFor="course-search">Search courses</label>
          <MagnifyingGlassIcon />
          <input id="course-search" type="search" placeholder="Search courses…" value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
        </div>
        <label className="catalogue-passport-filter">Passport
          <select value={filters.passport} onChange={e => updateFilter('passport', e.target.value)}><option value="">All passports</option>{CAREER_PASSPORT_DEFINITIONS.map(passport => <option key={passport.id} value={passport.id}>{passport.title}</option>)}</select>
        </label>
        <button type="button" className="catalogue-more-filters" aria-expanded={moreFilters} aria-controls="catalogue-secondary-filters" onClick={() => setMoreFilters(value => !value)}>{moreFilters ? 'Fewer filters' : 'More filters'}{[filters.type, filters.campus, filters.nqf].filter(Boolean).length > 0 && ` (${[filters.type, filters.campus, filters.nqf].filter(Boolean).length} active)`}</button>
        <div id="catalogue-secondary-filters" className={`catalogue-secondary-filters ${moreFilters ? 'is-open' : ''}`}>
        <label>Programme type<select value={filters.type} onChange={e => updateFilter('type', e.target.value)}><option value="">All programmes</option><option value="course">Courses</option><option value="short_course">Short courses</option></select></label>
        <label>Location<select value={filters.campus} onChange={e => updateFilter('campus', e.target.value)}><option value="">All locations</option>{campuses.map(campus => <option key={campus.code} value={campus.code}>{campus.name}</option>)}</select></label>
        <label>NQF level<select value={filters.nqf} onChange={e => updateFilter('nqf', e.target.value)}><option value="">All levels</option>{levels.map(level => <option key={level} value={level}>Level {level}</option>)}</select></label>
        </div>
      </section>
      <div className="catalogue-toolbar">
        <div aria-live="polite"><strong>{loading ? 'Loading courses…' : `${filtered.length} course${filtered.length === 1 ? '' : 's'}`}</strong>{hasFilters && <button type="button" className="catalogue-clear" onClick={clearFilters}>Clear filters</button>}</div>
        <div className="catalogue-layout" role="group" aria-label="Course layout">{[['cards', Squares2X2Icon, 'Cards'], ['list', Bars3Icon, 'List']].map(([value, Icon, label]) => <button key={value} type="button" aria-pressed={layout === value} onClick={() => setLayout(value)}><Icon />{label}</button>)}</div>
      </div>
      {notice && <div className="catalogue-notice" role="status">{notice}{!isAuthenticated && <Link to="/login">Sign in</Link>}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss notification">×</button></div>}
      {error ? <div className="catalogue-empty" role="alert"><h2>Courses are temporarily unavailable</h2><p>{error}</p><button className="catalogue-primary-action" onClick={loadCourses}>Try again</button></div>
        : loading ? <div className="catalogue-empty" role="status">Finding your learning opportunities…</div>
        : !filtered.length ? <div className="catalogue-empty"><MagnifyingGlassIcon /><h2>No courses found</h2><p>Try another search or remove a filter to see more courses.</p><button className="catalogue-primary-action" onClick={clearFilters}>Clear filters</button></div>
        : <div className={`catalogue-grid catalogue-grid--${layout}`}>{filtered.slice(0, limit).map(course => <CourseCard key={course.id} course={course} application={registrations[course.id]} saved={Boolean(wishlist[course.id])} saving={saving !== null} onSave={toggleSave} />)}</div>}
      {!loading && !error && filtered.length > limit && <div className="catalogue-load-more"><p>Showing {limit} of {filtered.length} courses</p><button type="button" className="catalogue-secondary-action" onClick={() => setLimit(value => value + 24)}>Show more courses</button></div>}
    </div>
  </div>;
};
export default Courses;
